/**
 * The memory globe's colours (D340), taken from our own tokens: a department wears one of the seven view tones
 * (`--tone-*` in index.css) and the four layers keep the ARMS tier colours, so the globe, the rings and the map agree.
 *
 * Reskin, per RUBRIC Agentic OS v2's REDESIGN.md (Jay E, RoboNuggets, MIT): one accent as a budget (ours is the teal,
 * spent on the selection), muted text carrying the design. Nothing here is the source's orange.
 *
 * `mixHex` and `shadeHex` are pure; `resolveTone` reads the live token, so it needs a DOM.
 */
import { type ArmsGrouping, groupColour } from './armsGroupingModel.js';
import { DEPARTMENT_TONES, type DepartmentTone, GLOBE_LAYERS, type GlobeDepartment, type GlobeLayer, layerColour } from './memoryGlobeModel.js';

/** What the tones are where the page cannot be asked (a test, a first paint): the same values as index.css. */
export const TONE_FALLBACK: Record<DepartmentTone, string> = {
  teal: '#2bb8a6',
  violet: '#8a6fe0',
  amber: '#e6a323',
  blue: '#3f8be6',
  rose: '#e4577a',
  green: '#3fbf6b',
  slate: '#8b94a6',
};

const clamp255 = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? [...h].map((c) => c + c).join('') : h.padEnd(6, '0').slice(0, 6);
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((c) => clamp255(c).toString(16).padStart(2, '0')).join('')}`;
}

/** `a` towards `b` by `t` (0 to 1). */
export function mixHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** The tone as it is (0), lighter (1) or darker (2): what a department past the seventh wears. */
export function shadeHex(hex: string, shade: number): string {
  if (shade === 1) return mixHex(hex, '#ffffff', 0.38);
  if (shade === 2) return mixHex(hex, '#000000', 0.34);
  return hex;
}

/** A CSS colour (oklch included) as `#rrggbb`, by painting one pixel; undefined where no canvas answers. */
export function cssToHex(css: string): string | undefined {
  if (typeof document === 'undefined' || !css) return undefined;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return undefined;
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = '#010203';
    ctx.fillStyle = css;
    ctx.fillRect(0, 0, 1, 1);
    const d = ctx.getImageData(0, 0, 1, 1).data;
    if (d[3] === 0) return undefined;
    return rgbToHex(d[0], d[1], d[2]);
  } catch {
    return undefined;
  }
}

export function resolveTone(tone: DepartmentTone, el: Element | null = typeof document === 'undefined' ? null : document.documentElement): string {
  if (!el || typeof getComputedStyle === 'undefined') return TONE_FALLBACK[tone];
  return cssToHex(getComputedStyle(el).getPropertyValue(`--tone-${tone}`).trim()) ?? TONE_FALLBACK[tone];
}

export interface GlobeColours {
  layer: Record<GlobeLayer, string>;
  /** By department index. */
  dept: string[];
  /** The accent, spent on the selection and the centre. */
  accent: string;
  /** Text and hairlines on the stage. */
  text: string;
  muted: string;
  /** Dimmed nodes. */
  dim: string;
}

export function resolveColours(departments: readonly GlobeDepartment[], el: Element | null, grouping: ArmsGrouping = 'tiers'): GlobeColours {
  const tones = Object.fromEntries(DEPARTMENT_TONES.map((t) => [t, resolveTone(t, el)])) as Record<DepartmentTone, string>;
  // The stage keeps its own night ground in both themes (index.css `.arms-stage`), so the globe is painted for the dark.
  const dark = true;
  return {
    layer: Object.fromEntries(GLOBE_LAYERS.map((k) => [k, grouping === 'themes' ? groupColour('themes', k) : layerColour(k)])) as Record<GlobeLayer, string>,
    dept: departments.map((d) => shadeHex(tones[d.tone], d.shade)),
    accent: tones.teal,
    text: dark ? '#e6e9ee' : '#1c2430',
    muted: dark ? '#8b94a6' : '#5b6575',
    dim: dark ? '#5b6372' : '#b9c0cc',
  };
}

/** The colour of a node: its layer's, or its department's for memory. */
export function nodeColour(c: GlobeColours, layer: GlobeLayer, deptIndex: number): string {
  return layer === 'memory' && deptIndex >= 0 ? (c.dept[deptIndex] ?? c.layer.memory) : c.layer[layer];
}
